// Developer: Ankit Gupta
/**
 * Choose a picture without a photo: a smiley, an animal or a thing, or one's initials, on a colour.
 *
 * The choice is drawn in the browser into the same small square picture an uploaded photo becomes,
 * and saved the same way, so it shows wherever a photo does (beside one's messages, the rail, the
 * profile) and the server keeps checking one kind of picture only. How an emoji looks follows the
 * device's own emoji font, as everywhere else it is typed.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Segmented } from 'antd';
import { useT } from '@/i18n';
import { canvasPicture } from '@/pages/Chat/pictures';

const SETS: Record<'smileys' | 'animals' | 'things', string[]> = {
  smileys: ['😀', '😄', '😊', '🙂', '😉', '😎', '🤓', '🧐', '😇', '🤠', '🥳', '🤩', '😌', '🤗', '🙃', '😺'],
  animals: ['🦊', '🐼', '🐯', '🦁', '🐨', '🐻', '🐧', '🦉', '🐙', '🐬', '🦄', '🐝', '🐢', '🦋', '🐳', '🦅'],
  things: ['🚀', '⭐', '🌙', '🔥', '🌴', '☕', '🎯', '🎧', '🧠', '💡', '🌍', '⚡', '🎨', '📊', '🛠️', '🏔️'],
};

// light enough behind an emoji, strong enough behind white initials; the first is AAFAQ red
const COLOURS = ['#dc2626', '#ea580c', '#d97706', '#16a34a', '#0d9488', '#0284c7', '#4f46e5', '#9333ea', '#db2777', '#475569'];
const SOFT = (hex: string) => `${hex}26`; // the colour at 15%, behind an emoji

type Kind = keyof typeof SETS | 'initials';

const SIZE = 256;

function draw(canvas: HTMLCanvasElement, kind: Kind, glyph: string, colour: string, initials: string) {
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, SIZE, SIZE);
  if (kind === 'initials') {
    const g = ctx.createLinearGradient(0, 0, SIZE, SIZE);
    g.addColorStop(0, colour);
    g.addColorStop(1, shade(colour, -0.28));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.fillStyle = '#ffffff';
    ctx.font = `600 ${initials.length > 1 ? 104 : 124}px Outfit, "Noto Sans", "Segoe UI", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, SIZE / 2, SIZE / 2 + 6);
    return;
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = SOFT(colour);
  ctx.fillRect(0, 0, SIZE, SIZE);
  // an emoji takes the fill's opacity: drawn in the 15% background colour it came out faded
  ctx.fillStyle = '#000000';
  ctx.font = '150px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(glyph, SIZE / 2, SIZE / 2 + 10);
}

/** A hex colour made darker (negative) or lighter (positive) by a share. */
function shade(hex: string, by: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(by < 0 ? c * (1 + by) : c + (255 - c) * by)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => f(c).toString(16).padStart(2, '0')).join('')}`;
}

export function AvatarPicker({ open, initials, saving, onClose, onPick }: {
  open: boolean;
  initials: string;
  saving: boolean;
  onClose: () => void;
  onPick: (dataUrl: string) => void;
}) {
  const { t } = useT('account');
  const [kind, setKind] = useState<Kind>('smileys');
  const [glyph, setGlyph] = useState(SETS.smileys[0]);
  const [colour, setColour] = useState(COLOURS[0]);
  const [error, setError] = useState<string | null>(null);
  const preview = useRef<HTMLCanvasElement>(null);
  const letters = useMemo(() => initials.slice(0, 2) || '?', [initials]);
  useEffect(() => {
    if (open && preview.current) draw(preview.current, kind, glyph, colour, letters);
  }, [open, kind, glyph, colour, letters]);
  const save = () => {
    const c = document.createElement('canvas');
    draw(c, kind, glyph, colour, letters);
    try {
      setError(null);
      onPick(canvasPicture(c));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('profile.avatar.title')}
      width={560}
      destroyOnClose
      footer={[
        <Button key="c" onClick={onClose}>{t('profile.avatar.cancel')}</Button>,
        <Button key="s" type="primary" loading={saving} onClick={save}>{t('profile.avatar.use')}</Button>,
      ]}
    >
      <div className="avatar-pick">
        <div className="avatar-pick-top">
          <canvas ref={preview} className="avatar-pick-preview" aria-label={t('profile.avatar.preview')} role="img" />
          <div className="avatar-pick-colours" role="radiogroup" aria-label={t('profile.avatar.colour')}>
            {COLOURS.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={c === colour} aria-label={c} className={`avatar-pick-colour${c === colour ? ' is-on' : ''}`} style={{ background: c }} onClick={() => setColour(c)} />
            ))}
          </div>
        </div>
        <Segmented
          block
          value={kind}
          onChange={(v) => { const k = v as Kind; setKind(k); if (k !== 'initials') setGlyph(SETS[k][0]); }}
          options={(['smileys', 'animals', 'things', 'initials'] as Kind[]).map((k) => ({ value: k, label: t(`profile.avatar.${k}`) }))}
        />
        {kind === 'initials' ? (
          <p className="avatar-pick-note">{t('profile.avatar.initialsNote', { initials: letters })}</p>
        ) : (
          <div className="avatar-pick-grid" role="radiogroup" aria-label={t(`profile.avatar.${kind}`)}>
            {SETS[kind].map((g) => (
              <button key={g} type="button" role="radio" aria-checked={g === glyph} className={`avatar-pick-glyph${g === glyph ? ' is-on' : ''}`} style={g === glyph ? { background: SOFT(colour), borderColor: colour } : undefined} onClick={() => setGlyph(g)}>
                {g}
              </button>
            ))}
          </div>
        )}
        {error && <p className="avatar-pick-error">{error}</p>}
      </div>
    </Modal>
  );
}
